import { ColorField, SelectField } from "@prismicio/types"
import classNames from "classnames"
import useEmblaCarousel from "embla-carousel-react"
import { ButtonHTMLAttributes, PropsWithChildren } from "react"

import { ArrowIcon } from "../components/Icon/Arrow"
import { usePrevNextButtons } from "../hooks"

const alignmentMap = {
    right: "text-right",
    center: "text-center",
    left: "text-left",
}
export const ProductsContainer = ({
    backgroundColor,
    alignment,
    titleColor,
    descriptionColor,
    title,
    description,
    children,
    className,
}: PropsWithChildren<{
    backgroundColor: ColorField
    descriptionColor: ColorField
    titleColor: ColorField
    alignment: SelectField<"right" | "center" | "left">
    title: string
    description: JSX.Element | string
    className: string
}>) => (
    <div className={classNames(className, "text-gray-base")} style={backgroundColor ? { backgroundColor } : {}}>
        <div
            className={classNames(
                "mb-2 pt-5 text-3xl font-bold",
                alignmentMap[alignment || "left"] || alignmentMap.left
            )}
            style={titleColor ? { color: titleColor } : {}}>
            <div>{title}</div>
        </div>
        <div
            className={classNames("mb-2 text-base", alignmentMap[alignment || "left"] || alignmentMap.left)}
            style={descriptionColor ? { color: descriptionColor } : {}}>
            {description}
        </div>
        {children}
    </div>
)
const slideSizeMap: Record<string, string> = {
    4: "lg:basis-1/4",
    5: "lg:basis-1/5",
    6: "lg:basis-1/6",
}
export const ProductCarousel = <T extends object>({
    items,
    displayNumber,
    CarouselItem,
    Button,
    CarouselContainer,
}: {
    items: T[]
    displayNumber: number
    CarouselItem: (props: { item: T; className: string }) => JSX.Element
    CarouselContainer: (props: PropsWithChildren<{ className: string }>) => JSX.Element
    Button: (props: PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>>) => JSX.Element
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start" })

    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi)
    return (
        <div className="relative px-6 py-3 lg:px-0">
            <div ref={emblaRef} className="w-full overflow-hidden">
                <CarouselContainer className="flex w-full touch-pan-y">
                    {items.map((item, index) => (
                        <CarouselItem
                            key={index}
                            item={item}
                            className={classNames("shrink-0 grow-0 basis-[50%]", slideSizeMap[displayNumber])}
                        />
                    ))}
                </CarouselContainer>
            </div>
            <Button
                className={classNames(
                    "absolute top-[38%] left-2.5 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow disabled:hidden lg:top-[45%] lg:h-10 lg:w-10"
                )}
                disabled={prevBtnDisabled}
                onClick={onPrevButtonClick}
                aria-label="Previous">
                <ArrowIcon height={20} width={20} className="lg:scale-125" />
            </Button>
            <Button
                className={classNames(
                    "absolute top-[38%] right-2.5 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow disabled:hidden lg:top-[45%] lg:h-10 lg:w-10"
                )}
                disabled={nextBtnDisabled}
                onClick={onNextButtonClick}
                aria-label="Next">
                <ArrowIcon height={20} width={20} className="translate-x-0.5 rotate-180 lg:scale-125" />
            </Button>
        </div>
    )
}
